import { AutomationStatus } from "../../config/enums";
import { administratorRoles } from "../../config/roles";
import { PageBadge, RecordDefinition } from "../recordDefinition.types";
import { automationPageConfig } from "./automationPageConfig";
import { executionEntityName } from "./automationExecutionTabs";
import {
  automationExecutionTableActions,
  readExecutionRows,
} from "./automationExecutions";
import {
  automationSaveMenu,
  newAutomationRecord,
  toAutomationPayload,
} from "./automationRecord";
import {
  automationBehaviours,
  automationValidators,
  automationVisibilityRules,
  isActiveAutomation,
} from "./automationRules";
import { AutomationDeliveryCard } from "./automationLazy";
import { AutomationScheduleSummary } from "./AutomationScheduleSummary";
import { AutomationStatistic } from "./AutomationStatistic";
import { AutomationTypeSummary } from "./AutomationTypeSummary";
import { AutomationUnpublishModal } from "./AutomationUnpublishModal";

const statusLabels: Record<number, { label: string; variant: string }> = {
  [AutomationStatus.Unpublished]: { label: "Unpublished", variant: "info" },
  [AutomationStatus.Published]: { label: "Published", variant: "success" },
  [AutomationStatus.Testing]: { label: "Testing", variant: "warning" },
};

const statusOf = (record: Record<string, unknown>) =>
  statusLabels[Number(record.status)] ?? statusLabels[AutomationStatus.Unpublished];

const badges: PageBadge[] = [
  {
    id: "status",
    label: (record) => statusOf(record).label,
    variant: (record) => statusOf(record).variant,
  },
];

export const automationRecordDefinition: RecordDefinition = {
  path: "/automation",
  entityName: "dwautomation",
  listRoute: "/automation-list",
  backTitle: "Back to automations",
  allowedRoles: administratorRoles,
  config: automationPageConfig,
  // The warehouse holds this record, and the view resolver never sees it.
  authorable: false,
  identity: {
    titleFields: ["name"],
    fallbackTitle: "Automation",
    newRecordTitle: "New Automation",
  },
  badges,
  newRecord: newAutomationRecord,
  toSavePayload: toAutomationPayload,
  saveMenu: automationSaveMenu,
  editableWhen: "automationEditable",
  visibilityRules: automationVisibilityRules,
  fieldValidators: automationValidators,
  fieldBehaviours: automationBehaviours,
  fieldComponents: {
    automationStatistic: AutomationStatistic,
    automationDeliveryCard: AutomationDeliveryCard,
    automationTypeSummary: AutomationTypeSummary,
    automationScheduleSummary: AutomationScheduleSummary,
  },
  relatedReads: {
    [executionEntityName]: (automationId, dispatch) =>
      readExecutionRows(dispatch, automationId),
  },
  relatedTableActions: automationExecutionTableActions,
  footerActions: [
    {
      id: "unpublish",
      label: "Unpublish",
      variant: "danger",
      visible: isActiveAutomation,
      modal: AutomationUnpublishModal,
    },
  ],
  // A published automation is read-only, and the page bar's back button leaves it.
  hideCancelWhenReadOnly: true,
};
